/* eslint-disable no-restricted-imports */
import { Box, Button } from "@chakra-ui/react";
import { useMemo } from "react";
import { RiArrowUpDownLine } from "react-icons/ri";
import { SearchableMenuPopover } from "src/components/Popovers/SearchableMenuPopover";
import { HighlightedText } from "src/components/Text/HighlightedText";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useEditFormTemplateContext } from "../../../context";

type Group = {
  group: string;
  items: QuestionItem[];
};
type QuestionItem =
  | {
      type: "Question";
      key: string;
      label: string;
    }
  | {
      type: "EmptySection";
      key: string;
      label: string;
      sectionId: string;
    };
type Props = { questionId: string; isDisabled: boolean };
export const MoveQuestionPopover: React.FC<Props> = ({
  questionId,
  isDisabled,
}) => {
  const context = useEditFormTemplateContext();
  const sections = context.state.get("draft").sections;
  const questions = sections.flatMap((section) =>
    "questions" in section ? section.questions : []
  );

  const items = useMemo(
    () =>
      sections.flatMap((section): Group[] => {
        if (
          section.type === "DisclaimerSection" ||
          section.type === "SchoolRankingSection"
        ) {
          return [] as Group[];
        }

        if (section.questions.length === 0) {
          // empty section
          return [
            {
              group: section.title,
              items: [
                {
                  key: section.id,
                  label: "Add to empty section",
                  type: "EmptySection",
                  sectionId: section.id,
                } satisfies QuestionItem,
              ],
            },
          ];
        }

        return [
          {
            group: section.title,
            items: section.questions.flatMap((question) => {
              if (question.id === questionId) {
                return [];
              }
              return [
                {
                  key: question.id,
                  label: question.question,
                  type: "Question",
                } satisfies QuestionItem,
              ];
            }),
          },
        ];
      }),
    [questionId, sections]
  );
  const toast = useAvelaToast();

  if (questions.length <= 1 && sections.length <= 1) {
    return null;
  }

  return (
    <SearchableMenuPopover<QuestionItem>
      renderItem={(item: QuestionItem, keyword) => {
        if (item.type === "EmptySection") {
          return (
            <Box as="span" color="blackAlpha.600" fontStyle="italic">
              <HighlightedText invert text={item.label} keyword={keyword} />
            </Box>
          );
        }
        return <HighlightedText invert text={item.label} keyword={keyword} />;
      }}
      title="Move before question"
      items={items}
      popoverTrigger={
        <Button
          leftIcon={<RiArrowUpDownLine />}
          variant="outline"
          colorScheme="gray"
          isDisabled={isDisabled}
        >
          Move question
        </Button>
      }
      onItemSelected={(selectedQuestion) => {
        if (selectedQuestion.type === "EmptySection") {
          context.dispatch({
            type: "AppendQuestionToSection",
            questionId: questionId,
            sectionId: selectedQuestion.sectionId,
          });
        } else {
          context.dispatch({
            type: "MoveQuestion",
            questionId: questionId,
            beforeQuestionId: selectedQuestion.key,
          });
        }

        toast({
          title: "Question moved",
        });
      }}
    />
  );
};
